import * as THREE from "../../libs/three.js/build/three.module.js";
import {MouseIntent} from "../navigation/MouseIntent.js";

// Motoren bag cursor-kuglen (CursorPointMarker) og den dynamiske streg (RubberBand): finder hvilket punkt cursoren peger på og
// melder det til DigitizationModule, som flytter kuglen og streg-enden.
//
// Hvorfor ikke bare et pick ved hver musebevægelse: punktsky-pick'et (GPU-pick + læsning) er det dyre trin, og viewet tegner KUN, når
// det er markeret "dirty" (viewer.needsRedraw) - en markør, der flyttes uden at bede om gen-tegning, dukker kun op, når noget andet
// tilfældigvis udløser en tegning. Derfor, med samme princip som Overfladedetektionens disk (navigation/SurfaceDetection.js):
//  - Punktet FØLGER MUSEN HVER FRAME: det placeres i (musens stråle) x (planen gennem det sidst målte punkt, vendt mod kameraet).
//    Selve pick'et kører kun for at opdatere planen: omkostnings-tilpasset (pick-tid / 0,3, 16-100 ms), højst hvert 120. ms mens
//    musen fejer hurtigt (MouseIntent), og straks når musen standser. Glatheden er dermed uafhængig af pick-raten.
//  - En gen-tegning bestilles, præcis når punktet flytter sig eller forsvinder.
//  - Et snap-mål (billigt) vinder over pick'et, som i _pickPlacementPosition(), og holdes, mens musen er tæt på det.
// Klikket selv beregner stadig sin egen, friske position (_pickPlacementPosition) - tracker'en er kun visning.

const MIN_PICK_INTERVAL_MS = 16;
const MAX_PICK_INTERVAL_MS = 100;
const FAST_PICK_INTERVAL_MS = 120; // pick-rate mens musen fejer hurtigt (>= 800 px/s)
const TARGET_DUTY = 0.3;           // et pick må højst optage ~30 % af hovedtråden
const MIN_MOVE_PX = 2;             // under 2 px bevægelse siden sidste pick: intet nyt pick
const SNAP_HOLD_PX = 6;            // et snappet punkt holdes, så længe musen er inden for så mange px af det

const now = () => (typeof performance !== "undefined" ? performance.now() : Date.now());

export class CursorTracker{
	// onChange(position|null): kaldes når det viste punkt flytter sig (position er genbrugt - kopiér den, hvis den gemmes) eller
	// forsvinder (null).
	constructor(dm, onChange){
		this.dm = dm;
		this.onChange = onChange;
		this.intent = new MouseIntent();

		this.active = false;
		this.mouse = null;      // canvas-relativ mus {x, y}
		this.client = null;     // {clientX, clientY} til pick'et
		this.plane = null;      // {point, mx, my, snapped}: sidst målte punkt og musens position dengang
		this.dirtyPick = false;
		this.lastPickAt = 0;
		this.lastPickCost = 0;
		this.lastPickMouse = {x: -1e9, y: -1e9};
		this.shown = false;
		this.position = new THREE.Vector3();

		this._raycaster = new THREE.Raycaster();
		this._plane = new THREE.Plane();
		this._dir = new THREE.Vector3();
		this._out = new THREE.Vector3();
		this._ndc = new THREE.Vector2();
	}

	// musen er bevæget over canvas med et aktivt værktøj og ingen museknap nede
	moved(e){
		this.mouse = this.dm._toCanvasMouse(e);
		this.client = {clientX: e.clientX, clientY: e.clientY};
		this.intent.push(this.mouse.x, this.mouse.y, now());
		this.active = true;
		this.dirtyPick = true;
	}

	// værktøjet er ikke længere aktivt / musen forlod canvas / orbit-pan begyndte: skjul punktet
	stop(){
		this.active = false;
		this.plane = null;
		this._emit(null);
	}

	// Er et punkt vist? (koordinatboksen kan så bruge det i stedet for et ekstra pick)
	isTracking(){
		return this.shown;
	}

	// kaldes hver frame (DigitizationModule.update)
	frame(){
		if(!this.active) return;

		const t = now();
		const state = this.intent.update(t);
		if(state.resumed){ this.lastPickAt = 0; this.dirtyPick = true; }

		if(this.dirtyPick){
			const base = Math.min(MAX_PICK_INTERVAL_MS, Math.max(MIN_PICK_INTERVAL_MS, this.lastPickCost / TARGET_DUTY));
			const interval = state.fast ? Math.max(base, FAST_PICK_INTERVAL_MS) : base;
			if(t - this.lastPickAt >= interval){
				const moved = Math.abs(this.mouse.x - this.lastPickMouse.x) + Math.abs(this.mouse.y - this.lastPickMouse.y);
				this.dirtyPick = false;
				if(moved >= MIN_MOVE_PX || this.plane === null) this._pick();
			}
		}

		if(!this.plane){
			this._emit(null);
			return;
		}
		this._follow();
	}

	// Det dyre trin: snap-mål (billigt) eller punktsky-pick. Måler sin egen pris, så intervallet tilpasser sig.
	_pick(){
		const dm = this.dm;
		const mouse = {x: this.mouse.x, y: this.mouse.y};
		const t0 = now();

		let point = null;
		let snapped = false;
		const target = dm.findSnapTarget(mouse);
		if(target){
			point = target.clone();
			snapped = true;
		}else if(this.client){
			point = dm._pickPointCloudPosition(this.client);
		}

		const cost = now() - t0;
		this.lastPickCost = this.lastPickCost === 0 ? cost : this.lastPickCost * 0.6 + cost * 0.4;
		this.lastPickAt = now();
		this.lastPickMouse.x = mouse.x;
		this.lastPickMouse.y = mouse.y;

		// intet punkt under musen: ingen markør (som Overfladedetektionens disk), til næste pick finder en overflade
		this.plane = point ? {point, mx: mouse.x, my: mouse.y, snapped} : null;
	}

	// Punktets position nu: det målte punkt, flyttet med musen langs en kamera-vendt plan gennem det.
	_follow(){
		const plane = this.plane;
		const mouse = this.mouse;
		const moved = Math.hypot(mouse.x - plane.mx, mouse.y - plane.my);

		let x = plane.point.x, y = plane.point.y, z = plane.point.z;
		if(!(plane.snapped && moved <= SNAP_HOLD_PX) && moved >= 0.5){
			const viewer = this.dm.viewer;
			const camera = viewer.scene.getActiveCamera();
			const dom = viewer.renderer.domElement;
			this._ndc.set((mouse.x / dom.clientWidth) * 2 - 1, -(mouse.y / dom.clientHeight) * 2 + 1);
			this._raycaster.setFromCamera(this._ndc, camera);
			camera.getWorldDirection(this._dir);
			this._plane.setFromNormalAndCoplanarPoint(this._dir, plane.point);
			if(this._raycaster.ray.intersectPlane(this._plane, this._out)){
				x = this._out.x; y = this._out.y; z = this._out.z;
			}
		}

		const p = this.position;
		if(this.shown && p.x === x && p.y === y && p.z === z) return;
		p.set(x, y, z);
		this._emit(p);
	}

	_emit(position){
		if(!position){
			if(!this.shown) return;
			this.shown = false;
		}else{
			this.shown = true;
		}
		this.onChange(position);
		this._redraw();
	}

	_redraw(){
		const viewer = this.dm.viewer;
		if(typeof viewer._requestRedraw === "function") viewer._requestRedraw(); else viewer.needsRedraw = true;
	}
}
